import { useState } from "react";
import { Loader2, Pencil, Plus, Power, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import InvitationsPanel from "../../components/company/InvitationsPanel";
import AccessFields, { emptyAccess } from "../../components/company/AccessFields";
import { fmtDate } from "../../lib/labels";

const pick = (m) => ({ role: m.role, agency_ids: m.agency_ids || [], service_ids: m.service_ids || [],
  granted_permissions: m.granted_permissions || [], revoked_permissions: m.revoked_permissions || [] });

const MemberDialog = ({ member, meta, agencies, services, onClose, onSaved }) => {
  const [access, setAccess] = useState(member ? pick(member) : emptyAccess());
  const [id, setId] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (member) await api.patch(`/company/team/${member.id}`, access);
      else await api.post("/company/team", { ...id, ...access, password: id.password || null });
      toast.success(member ? "Accès mis à jour" : "Collaborateur ajouté"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="member-dialog">
        <DialogHeader><DialogTitle>{member ? `Rôle et accès — ${member.name}` : "Ajouter un collaborateur"}</DialogTitle></DialogHeader>
        {!member && (
          <div className="grid gap-2 sm:grid-cols-3">
            <Input placeholder="Nom complet" value={id.name} onChange={(e) => setId({ ...id, name: e.target.value })} data-testid="member-name-input" />
            <Input type="email" placeholder="Email" value={id.email} onChange={(e) => setId({ ...id, email: e.target.value })} data-testid="member-email-input" />
            <Input type="password" placeholder="Mot de passe initial (nouveau compte)" value={id.password} onChange={(e) => setId({ ...id, password: e.target.value })} data-testid="member-password-input" />
          </div>
        )}
        <AccessFields value={access} onChange={setAccess} meta={meta} agencies={agencies} services={services} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="member-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="member-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const scopeText = (m, agencies, services) => {
  const names = [...(m.agency_ids || []).map((x) => agencies?.find((a) => a.id === x)?.name), ...(m.service_ids || []).map((x) => services?.find((s) => s.id === x)?.name)].filter(Boolean);
  return names.length ? names.join(", ") : ["owner", "manager"].includes(m.role) ? "Toute l'entreprise" : "Toute l'entreprise (non restreint)";
};

const Row = ({ m, me, can, onEdit, onStatus, agencies, services }) => (
  <div className={`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center ${m.status === "disabled" ? "opacity-60" : ""}`} data-testid={`team-member-${m.id}`}>
    <div className="flex-1">
      <p className="font-medium">{m.name}{m.user_id === me && <span className="ml-2 text-xs text-slate-400">(vous)</span>}</p>
      <p className="text-xs text-slate-500">{m.email}</p>
      <p className="mt-1 text-xs text-slate-500" data-testid={`team-scope-${m.id}`}>Affectations : {scopeText(m, agencies, services)}</p>
    </div>
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="rounded-full bg-slate-100 px-3 py-1 font-medium" data-testid={`team-role-${m.id}`}>{m.role_label}</span>
      <span className={`rounded-full px-2 py-0.5 ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={`team-status-${m.id}`}>{m.status === "active" ? "Actif" : "Désactivé"}</span>
      <span className="text-slate-400" data-testid={`team-activity-${m.id}`}>Dernière activité : {m.last_activity ? fmtDate(m.last_activity) : "non disponible"}</span>
      {m.user_id !== me && can("team:update") && <Button size="sm" variant="outline" onClick={() => onEdit(m)} data-testid={`team-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
      {m.user_id !== me && can("team:deactivate") && <Button size="sm" variant="outline" onClick={() => onStatus(m)} data-testid={`team-toggle-${m.id}`}><Power className="mr-1 h-3.5 w-3.5" />{m.status === "active" ? "Désactiver" : "Réactiver"}</Button>}
    </div>
  </div>
);

export default function Team() {
  const { user } = useAuth();
  const { can } = useCompany();
  const { data, loading, error, reload } = useApi("/company/team");
  const meta = useApi("/company/permissions");
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  const [editing, setEditing] = useState(null);
  const status = async (m) => {
    try { await api.post(`/company/team/${m.id}/status`, { active: m.status !== "active" }); toast.success(m.status === "active" ? "Collaborateur désactivé" : "Collaborateur réactivé"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (loading || meta.loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div>
      <PageHeader title="Collaborateurs" description="Rôles, périmètres et permissions de votre équipe. Chaque droit est vérifié par le serveur."
        actions={can("team:invite") && <Button onClick={() => setEditing("new")} className="rounded-full" data-testid="team-add-button"><Plus className="mr-1.5 h-4 w-4" />Ajouter</Button>} />
      {!data.length ? <EmptyState icon={Users} title="Aucun collaborateur" /> : (
        <div className="card-surface divide-y" data-testid="team-list">
          {data.map((m) => <Row key={m.id} m={m} me={user?.id} can={can} onEdit={setEditing} onStatus={status} agencies={agencies.data} services={services.data} />)}
        </div>
      )}
      {can("team:invite") && meta.data && <InvitationsPanel meta={meta.data} agencies={agencies.data} services={services.data} />}
      {editing && meta.data && <MemberDialog member={editing === "new" ? null : editing} meta={meta.data} agencies={agencies.data} services={services.data}
        onClose={() => setEditing(null)} onSaved={reload} />}
    </div>
  );
}
